import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatPct, prettyQuarter } from '@/lib/format';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { QuarterRangeSlider } from './QuarterRangeSlider';
import { ChartSummary, Emph } from './ChartSummary';
import { QuarterTick, useQuarterWindow, profileChartStyles as s } from './ProfileSection';

interface HistoryPoint {
  quarter: string;
  churn_rate: number | null;
}

export interface ChurnRateChartProps {
  platform: string;
  history: HistoryPoint[];
  /** Average churn of all platforms, latest quarter — the dashed reference. */
  peerAverage?: number;
  peerCount?: number;
}

/** Churn rate over time against the average of every platform. */
export function ChurnRateChart({ platform, history, peerAverage, peerCount }: ChurnRateChartProps) {
  const allPoints = useMemo(
    () =>
      history
        .filter((h) => h.churn_rate != null)
        .map((h) => ({ label: prettyQuarter(h.quarter), churn: h.churn_rate as number })),
    [history],
  );
  const [[lo, hi], setRange] = useQuarterWindow(allPoints.length);
  const points = allPoints.slice(lo, hi + 1);
  const [loss, muted] = useResolvedColors(['var(--metric-loss)', 'var(--text-muted)']);
  const last = points[points.length - 1];
  const isLatest = hi === allPoints.length - 1;

  return (
    <>
      <ChartSummary className={s.summary}>
        {last ? (
          <>
            <Emph>{platform}</Emph> lost <Emph>{formatPct(last.churn)}</Emph> of its subscribers in{' '}
            <Emph>{last.label}</Emph>
            {isLatest && peerAverage !== undefined && (
              <>
                , {last.churn <= peerAverage ? 'below' : 'above'} the <Emph>{formatPct(peerAverage)}</Emph> average of{' '}
                {peerCount} platforms
              </>
            )}
            .
          </>
        ) : (
          '—'
        )}
      </ChartSummary>
      <div className={s.body}>
        {allPoints.length > 1 && (
          <QuarterRangeSlider
            count={allPoints.length}
            value={[lo, hi]}
            onChange={setRange}
            labels={allPoints.map((p) => p.label)}
          />
        )}
        {points.length ? (
          <>
            <div className={s.plot}>
              <span className={`${s.yAxisTitle} ${s.yAxisTitleLeft}`}>Churn Rate</span>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={points} margin={{ top: 16, right: 28, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={{ stroke: 'var(--border-default)' }}
                    tickLine={false}
                    interval={points.length > 16 ? 'preserveStartEnd' : 0}
                    height={46}
                    tick={QuarterTick}
                  />
                  <YAxis
                    className={s.axis}
                    axisLine={false}
                    tickLine={false}
                    width={44}
                    domain={[0, 'auto']}
                    tickFormatter={(v) => formatPct(Number(v), 0)}
                  />
                  <Tooltip
                    cursor={{ stroke: 'var(--border-strong)' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <ChartTooltip
                          title={label}
                          rows={[{ label: 'Churn rate', value: formatPct(Number(payload[0].value)), color: loss }]}
                        />
                      );
                    }}
                  />
                  {peerAverage !== undefined && (
                    <ReferenceLine y={peerAverage} stroke={muted} strokeDasharray="5 4" />
                  )}
                  <Line
                    dataKey="churn"
                    stroke={loss}
                    strokeWidth={2}
                    dot={{ r: 3, fill: loss, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
              <span />
            </div>
            <ChartLegend
              items={[
                { label: platform, color: loss, shape: 'line' },
                ...(peerAverage !== undefined
                  ? [{ label: `Average of ${peerCount} platforms (latest quarter)`, color: muted, shape: 'dash' as const }]
                  : []),
              ]}
            />
          </>
        ) : (
          <div className={s.empty}>No churn data available</div>
        )}
      </div>
    </>
  );
}
